import { gql, useMutation, useQuery } from '@apollo/client';
import { FeatureFlagTypes, type GoalTypes } from '@generated/graphql';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import { useConfirmationDialog } from '@ovComponents/4-module/workflowCompletion/components/useConfirmationDialog';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { CreateGoalVisual } from './createGoal.visual';
import { useGoalCreationMethodChoice } from './useGoalCreationMethodChoice';

const GET_EXISTING_GOALS = gql`
  query fetchGoals($input: FetchGoalsInput!) {
    fetchGoals(input: $input) {
      totalCount
      goals {
        id
        type
        name
        statistics {
          marketValueCents
          currency
        }
      }
    }
  }
`;

const CREATE_GOAL = gql`
  mutation createGoal($input: CreateGoalInput!) {
    createGoal(input: $input) {
      goal {
        id
        name
        type
      }
    }
  }
`;

const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
        name
        type
      }
    }
  }
`;

const FETCH_HOUSEHOLD_PRIMARY_USER = gql`
  query fetchHouseholdPrimaryUser($id: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $id }) {
      clientGroup {
        primaryUser {
          id: stringId
        }
      }
    }
  }
`;

export type GoalData = {
  name: string | null;
  type: string | null;
};

export type CreateGoalOptions = {
  title: TranslatedString;
  subtitle: TranslatedString;
  buildWealth: boolean;
  car: boolean;
  education: boolean;
  house: boolean;
  majorPurchase: boolean;
  other: boolean;
  parentalLeave: boolean;
  retirement: boolean;
  safetyNet: boolean;
  startABusiness: boolean;
  vacation: boolean;
  wedding: boolean;
  cashReserve: boolean;
  legacy: boolean;
  group: boolean;
  retirementIncome: boolean;
  goalCreationMethod: 'EXISTING_GOAL' | 'NEW_GOAL' | 'USER_CHOICE';
  callToActions: [];
};

export type GoalsByType = Partial<Record<GoalTypes, Goal[]>>;

export const CreateGoal = ({
  options: propsOptions,
  objectId,
  onNext,
  stepLoading,
  workflowCompletion,
  objectType = 'USER',
}: {
  options: CreateGoalOptions;
  objectId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
  objectType?: string;
}) => {
  const { t } = useTranslation(['client', 'workflowCompletions']);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const goalCreationMethodEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.GoalCreationMethod,
  );
  const options: typeof propsOptions = {
    ...propsOptions,
    goalCreationMethod: propsOptions.goalCreationMethod || 'EXISTING_GOAL',
  };

  const [goalData, setGoalData] = useState<GoalData>({
    name: workflowData.currentGoalName ?? '',
    type: workflowData.currentGoalType ?? null,
  });

  const { data: existingGoalsData, loading: loadingExistingGoals } = useQuery<{
    fetchGoals: { goals: Goal[] };
  }>(GET_EXISTING_GOALS, {
    variables: {
      input: {
        filter: {
          ...(objectType === 'HOUSEHOLD'
            ? { clientGroupId: objectId }
            : { userId: objectId }),
          state: 'ACTIVE',
        },
        pagination: {
          perPage: 1000, // a reasonably large number to avoid having to paginate
        },
      },
    },
  });

  const goalsByType = (existingGoalsData?.fetchGoals?.goals || []).reduce(
    (acc, goal) => {
      const type = goal.type as GoalTypes;
      const existingGoals = acc[type] || [];
      existingGoals.push(goal);
      acc[type] = existingGoals;
      return acc;
    },
    {} as GoalsByType,
  );

  const { data: householdPrimaryUser, loading: loadingHouseholdPrimaryUser } =
    useQuery(FETCH_HOUSEHOLD_PRIMARY_USER, {
      variables: { id: objectId },
      fetchPolicy: 'no-cache',
      skip: objectType !== 'HOUSEHOLD',
    });

  const userId =
    objectType === 'HOUSEHOLD'
      ? householdPrimaryUser?.fetchClientGroup?.clientGroup?.primaryUser?.id
      : objectId;

  const [createGoalMutation, { loading: createLoading }] =
    useMutation(CREATE_GOAL);
  const createGoal = async () => {
    const { data } = await createGoalMutation({
      variables: {
        input: {
          type: goalData.type,
          name: goalData.name || t(`goalType.${goalData.type}`),
          riskQuestion1: 'LEVEL_1',
          timeHorizon: 'VERY_SHORT_TERM',
          ...(objectType === 'HOUSEHOLD'
            ? {
                clientGroupId: objectId,
                userId,
              }
            : { userId: objectId }),
        },
      },
    });
    await setWorkflowData((prev) => ({
      ...prev,
      currentGoalId: data.createGoal.goal.id,
      currentGoalType: data.createGoal.goal.type,
      currentGoalName: data.createGoal.goal.name,
      currentUserId: userId,
      createdGoalIds: [...(prev.createdGoalIds || []), data.createGoal.goal.id],
    }));
    return data.createGoal.goal as Goal;
  };

  const [updateGoalMutation, { loading: updateLoading }] =
    useMutation(UPDATE_GOAL);
  const updateGoal = async (goalId: string, type: GoalTypes, name: string) => {
    const { data } = await updateGoalMutation({
      variables: {
        input: {
          goalId,
          type,
          name,
        },
      },
    });
    await setWorkflowData((prev) => ({
      ...prev,
      currentGoalName: data.updateGoal.goal.name,
      currentGoalType: data.updateGoal.goal.type,
      currentUserId: userId,
    }));
    return data.updateGoal.goal as Goal;
  };

  const { confirmationDialog, getUserConfirmation } = useConfirmationDialog();
  const { getGoalCreationMethodChoice, goalCreationMethodChoiceDialog } =
    useGoalCreationMethodChoice();
  const goalType = goalData.type as GoalTypes;
  const goalTypeDisplay = t(`goalType.${goalType}`);

  const onContinue = async () => {
    const activeGoals = goalsByType[goalType] || [];
    const goalCount = activeGoals.length;

    if (!goalCreationMethodEnabled) {
      const { currentGoalId } = workflowData;
      if (currentGoalId) {
        await updateGoal(
          currentGoalId,
          goalType,
          goalData.name || goalTypeDisplay,
        );
      } else {
        await createGoal();
      }
      await onNext();
      return;
    }

    switch (true) {
      case !!workflowData.currentGoalId: {
        await updateGoal(
          workflowData.currentGoalId as string,
          goalType,
          goalData.name || goalTypeDisplay,
        );
        break;
      }

      case !goalCount: {
        await createGoal();
        break;
      }

      case options.goalCreationMethod === 'NEW_GOAL': {
        const userConfirmation = await getUserConfirmation({
          title: t(
            'workflowCompletions:createGoal.confirmDialog.newGoalConfirmation.title',
          ),
          bodyText: t(
            'workflowCompletions:createGoal.confirmDialog.newGoalConfirmation.bodyText',
            {
              goalType: goalTypeDisplay,
            },
          ),
          autoConfirmKey: 'NEW_GOAL',
        });
        if (!userConfirmation.confirmed) {
          userConfirmation.closeDialog();
          return;
        }
        await createGoal();
        userConfirmation.closeDialog();
        break;
      }

      case options.goalCreationMethod === 'USER_CHOICE' || goalCount > 1: {
        const userChoiceResult = await getGoalCreationMethodChoice({
          goalType,
          activeGoals,
          allowNewGoal: options.goalCreationMethod !== 'EXISTING_GOAL',
        });
        const choice = userChoiceResult?.choice;
        if (!choice) {
          /** User cancelled the dialog */
          userChoiceResult.closeDialog();
          return;
        }
        switch (choice.method) {
          case 'NEW_GOAL': {
            await createGoal();
            break;
          }
          case 'EXISTING_GOAL': {
            const { goal } = choice;
            await setWorkflowData((prev) => ({
              ...prev,
              currentGoalId: goal.id,
              currentGoalType: goal.type,
              currentGoalName: goal.name,
            }));
          }
        }
        userChoiceResult.closeDialog();
        break;
      }

      case options.goalCreationMethod === 'EXISTING_GOAL': {
        const userConfirmation = await getUserConfirmation({
          title: t(
            'workflowCompletions:createGoal.confirmDialog.existingGoalConfirmation.title',
          ),
          bodyText: t(
            'workflowCompletions:createGoal.confirmDialog.existingGoalConfirmation.bodyText',
            {
              goalType: goalTypeDisplay,
            },
          ),
          autoConfirmKey: 'EXISTING_GOAL',
        });
        if (!userConfirmation.confirmed) {
          userConfirmation.closeDialog();
          return;
        }
        const goal = activeGoals.at(0) as Goal;
        await setWorkflowData((prev) => ({
          ...prev,
          currentGoalId: goal.id,
          currentGoalType: goal.type,
          currentGoalName: goal.name,
        }));
        userConfirmation.closeDialog();
        break;
      }
    }
    await onNext();
  };

  return (
    <CreateGoalVisual
      options={options}
      goalData={goalData}
      updateGoal={setGoalData}
      continueFunc={onContinue}
      loading={
        createLoading ||
        updateLoading ||
        stepLoading ||
        loadingExistingGoals ||
        loadingHouseholdPrimaryUser
      }
      workflowCompletion={workflowCompletion}
      goalsByType={goalsByType}
      confirmationDialog={confirmationDialog}
      goalCreationMethodChoiceDialog={goalCreationMethodChoiceDialog}
    />
  );
};
